import { Clock, Play, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function CaseCard({ data, userStatus, attemptsCount = 0 }) {
  const navigate = useNavigate();
  
  // data = ClinicalSimCaseResponse
  // userStatus = session data if doctor has attempted this case

  const { case_id, title, difficulty, category, is_emergency, max_duration_sec, patient, max_attempts = 5 } = data;
  const isCompleted = userStatus?.status === 'completed';
  const isInProgress = userStatus?.status === 'in_progress';
  
  const hasRemainingAttempts = attemptsCount < max_attempts || isInProgress;
  
  const handleStart = () => {
    if (!hasRemainingAttempts && !isCompleted) return;
    // If not started or in progress, we might navigate to a specific flow.
    // For Phase 0, we can just navigate to the session workspace.
    // But we need to create the session first if it doesn't exist.
    // For now we'll pass state.
    navigate(`/cs/sessions/new?caseId=${case_id}`);
  };

  const difficultyColors = {
    Beginner: 'bg-violet-100 text-violet-700 border-violet-200',
    Intermediate: 'bg-amber-100 text-amber-700 border-amber-200',
    Advanced: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <div className="flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow group">
      {/* Header */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex justify-between items-start mb-3">
          <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${difficultyColors[difficulty] || difficultyColors.Beginner}`}>
            {difficulty}
          </span>
          {is_emergency && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-50 text-red-600 border border-red-100 text-[10px] font-bold uppercase tracking-wider">
              <AlertCircle className="w-3 h-3" /> ER
            </span>
          )}
        </div>
        
        <h3 className="text-lg font-bold text-slate-800 line-clamp-2 leading-tight mb-1 group-hover:text-violet-600 transition-colors">
          {title}
        </h3>
        <p className="text-sm font-medium text-slate-500">{category}</p>
      </div>

      {/* Patient Snippet */}
      <div className="px-5 py-4 flex-1 bg-slate-50/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm">
            {patient.initials || 'PT'}
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700">{patient.name || 'Unknown Patient'}</p>
            <p className="text-xs text-slate-500">{patient.age} yrs • {patient.gender}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-white px-2 py-1.5 rounded border border-slate-100 w-max">
            <Clock className="w-3.5 h-3.5" />
            {Math.floor(max_duration_sec / 60)} min time limit
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-white px-2 py-1.5 rounded border border-slate-100 w-max">
            <RefreshCw className="w-3.5 h-3.5" />
            {attemptsCount} / {max_attempts} attempts
          </div>
        </div>
      </div>

      {/* Footer / CTA */}
      <div className="p-4 bg-white border-t border-slate-100 flex items-center justify-between">
        {isCompleted ? (
          <div className="flex items-center gap-1.5 text-sm font-bold text-violet-600">
            <CheckCircle2 className="w-4 h-4" />
            Completed
          </div>
        ) : isInProgress ? (
          <div className="flex items-center gap-1.5 text-sm font-bold text-amber-500">
            In Progress
          </div>
        ) : !hasRemainingAttempts ? (
          <div className="text-sm font-medium text-red-500">Limit Reached</div>
        ) : (
          <div className="text-sm font-medium text-slate-400">Not Started</div>
        )}

        <button 
          onClick={handleStart}
          disabled={!hasRemainingAttempts && !isCompleted}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
            isCompleted 
              ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' 
              : !hasRemainingAttempts
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-violet-500 hover:bg-violet-600 text-white shadow-sm hover:shadow-violet-500/25'
          }`}
        >
          {isCompleted ? 'Review Case' : isInProgress ? 'Resume Case' : !hasRemainingAttempts ? 'Limit Reached' : 'Start Case'}
          {!isCompleted && hasRemainingAttempts && <Play className="w-3.5 h-3.5 fill-current" />}
        </button>
      </div>
    </div>
  );
}
